import { Stepper } from '@mui/material';
import cx from 'classnames';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { pushSnackbar } from 'FRONTEND/actions/app';
import { getFormStepListAndIndex } from 'FRONTEND/components/Calendar/AppointmentForm/appointmentFormStepLists';
import AppointmentSupport from 'FRONTEND/components/Calendar/AppointmentSupport';
import Loader from 'FRONTEND/components/Loader';
import { createRequest as createAppointmentRequest } from 'FRONTEND/reducers/appointmentSlice';
import { listRequest as listAvailabilities } from 'FRONTEND/reducers/availabilitySlice';
import { formCleared, resetBookingForm } from 'FRONTEND/reducers/bookingSlice';
import { selectManagedExpertById } from 'FRONTEND/reducers/expertSlice';
import { listLocations, listLocationsWithSlot, selectHasLocationAndServiceRights, selectLocationById, selectLocationsWithSlots } from 'FRONTEND/reducers/locationSlice';
import { selectPatientById } from 'FRONTEND/reducers/patientSlice';
import { listResources } from 'FRONTEND/reducers/resourceSlice';
import { selectHasRight } from 'FRONTEND/reducers/userSlice';
import { selectAdditionalSelectedExpertIds } from 'FRONTEND/selectors/bookingSelectors';
import {
  selectAdditionalSelectedExperts,
  selectedExpertHasStripe,
  selectUserAndSelectedExpertsCommonTenantIds,
} from 'FRONTEND/selectors/expertSelectors';
import { useGetConsultationCodesForUserQuery } from 'FRONTEND/utils/backendApi/consultationCodesApi';
import {
  APPOINTMENT_DEFAULT_DURATION,
  CREATE_ERROR,
  OTHER,
  VIDEO_CONSULTATION,
  VIDEO_SPECIALIST_ASSESSMENT,
  FormTypes,
} from 'FRONTEND/utils/constants';
import useUpdateOnlyEffect from 'FRONTEND/utils/hooks/useUpdateOnlyEffect';
import i18n from 'FRONTEND/utils/i18n';
import { RIGHTS } from 'FRONTEND/utils/rights';
import { CALENDAR_PATH, UPCOMING_APPOINTMENTS_PATH } from 'FRONTEND/utils/routes';
import { getDurationSettingKey } from 'FRONTEND/utils/stringFormatting';
import { PJAX_MAIN_CONTAINER_SELECTOR } from 'LEGACY/constants';

const AppointmentForm = ({ forRightPanel, onCreateAppointmentSettled = null, isDialog }) => {
  const dispatch = useDispatch();
  const isSmallCalendar = useSelector((state) => state.app.isSmall);
  const isSmall = forRightPanel || isSmallCalendar;
  const hasLocationAndServiceRights = useSelector(selectHasLocationAndServiceRights);
  const selectedExpertId = useSelector((state) => state.booking.selectedExpertId);
  const hasAppointmentCreatePatientRight = useSelector((state) => selectHasRight(state, RIGHTS.APPOINTMENTS_CREATE_AS_PATIENT));
  const locationsWithSlots = useSelector(selectLocationsWithSlots);
  const hasAppointmentLocationRights = useSelector((state) => selectHasRight(state, RIGHTS.APPOINTMENTS_LOCATIONS));
  const showLocationStep = hasAppointmentLocationRights && locationsWithSlots.length > 0;

  // In case page is refreshed, re-call critical APIs for this component.
  useEffect(() => {
    if (hasLocationAndServiceRights) {
      // for forms where expert is selected at the start
      // used so we can filter out form types (Teleconsultation/Telestaff...) that do not have an availability associated on the expert
      if (selectedExpertId) {
        dispatch(listAvailabilities({ actorId: selectedExpertId, startDate: new Date().toISOString() }));
      }
      dispatch(listResources());
      dispatch(listLocations());
      dispatch(listLocationsWithSlot({ ignoreManagementRight: hasAppointmentCreatePatientRight }));
    }
  }, []);

  // Loading state management
  const isLoadingCreateAppointment = useSelector((state) => state.app.requestCounter[createAppointmentRequest.toString()]);
  const createAppointmentError = useSelector((state) => state.appointment.error === CREATE_ERROR);
  useUpdateOnlyEffect(() => {
    if (!isLoadingCreateAppointment) {
      if (isUnrestrictedForm && onCreateAppointmentSettled) {
        onCreateAppointmentSettled();
      } else if (isUnrestrictedForm || createAppointmentError) {
        // Regular form success navigation is handled by the appointment middleware (goToActorCalendar).
        // Navigate here only for unrestricted/patient forms and error cases.
        dispatch(resetBookingForm());
        $.pjax({ url: formType === FormTypes.PATIENT_FORM ? UPCOMING_APPOINTMENTS_PATH : CALENDAR_PATH, container: PJAX_MAIN_CONTAINER_SELECTOR });
        if (createAppointmentError) {
          dispatch(pushSnackbar({
            message: i18n._('An error occurred. Please retry later.'),
            variant: 'error',
          }));
        }
      }
    }
  }, [isLoadingCreateAppointment]);

  // Step navigation management
  const formType = useSelector((state) => state.booking.formType);
  const selectedExpert = useSelector((state) => selectManagedExpertById(state, state.booking.selectedExpertId));
  const additionalSelectedExpertList = useSelector(selectAdditionalSelectedExperts);
  const availableTypeList = useSelector((state) => state.booking.availableTypeList);
  const selectedType = useSelector((state) => state.booking.selectedType);
  const selectedPatient = useSelector((state) => selectPatientById(state, state.booking.selectedPatientId));
  const selectedLocation = useSelector((state) => selectLocationById(state, state.booking.selectedLocationId));
  const selectedSlot = useSelector((state) => state.booking.selectedSlot);
  const desiredDateTime = useSelector((state) => state.booking.desiredDateTime);
  const desiredDuration = useSelector((state) => state.booking.desiredDuration);
  const hasPayment = useSelector((state) => state.booking.hasPayment);
  const desiredConsultationCode = useSelector((state) => state.booking.desiredConsultationCode);
  const isPayLater = useSelector((state) => state.booking.isPayLater);
  const expertHasStripe = useSelector(selectedExpertHasStripe);
  const isBooth = useSelector((state) => state.booking.isBooth);
  const { data: expertConsultationCodes } = useGetConsultationCodesForUserQuery(selectedExpert?.id, { skip: !selectedExpert?.id });
  const hasExpertConsultationCodes = Boolean(expertConsultationCodes && expertConsultationCodes.length > 0);
  const { stepList, initialStepIndex, resetStepIndex } = getFormStepListAndIndex(formType, selectedType, {
    selectedExpert,
    additionalSelectedExpertList,
    availableTypeList,
    selectedType,
    selectedPatient,
    selectedLocation,
    selectedSlot,
    desiredDateTime,
    desiredDuration,
    expertHasStripe,
    hasExpertConsultationCodes,
    hasPayment,
    showLocationStep,
    desiredConsultationCode,
    isPayLater,
    isBooth,
  });
  const reason = useSelector((state) => state.booking.reason);
  const isPatientNotifEnabled = useSelector((state) => state.booking.isPatientNotifEnabled);
  const userId = useSelector((state) => state.user.id);
  const additionalSelectedExpertIdList = useSelector(selectAdditionalSelectedExpertIds);
  const isThirdPartyNotifEnabled = useSelector((state) => state.booking.isThirdPartyNotifEnabled);
  const thirdPartyEmailList = useSelector((state) => state.booking.thirdPartyEmailList);
  const lastStepIndex = stepList.length - 1;
  const [activeStepIndex, setActiveStepIndex] = useState(initialStepIndex);
  const shouldClearForm = useSelector((state) => state.booking.shouldClearForm);
  const isUnrestrictedForm = formType === FormTypes.UNRESTRICTED_FORM || formType === FormTypes.PATIENT_FORM;
  const tenantIds = useSelector(selectUserAndSelectedExpertsCommonTenantIds);
  const tenantList = useSelector((state) => state.user.tenantList);
  const isLocationFormPremiocareTenant = Boolean(
    formType === FormTypes.LOCATION_FORM &&
    selectedLocation?.tenantId &&
    tenantList[selectedLocation.tenantId]?.premiocare_cabinet_id,
  );

  useEffect(() => {
    if (shouldClearForm) {
      setActiveStepIndex(resetStepIndex);
      dispatch(formCleared());
    }
  }, [shouldClearForm, resetStepIndex]);

  useEffect(() => {
    setActiveStepIndex(initialStepIndex);
  }, [formType]);

  const nextStep = () => {
    if (activeStepIndex === lastStepIndex) {
      generateAppointmentPayload();
    } else {
      setActiveStepIndex(activeStepIndex + 1);
    }
  };
  const goBackToStep = (stepIndexToGo) => {
    setActiveStepIndex(stepIndexToGo);
  };

  const generateAppointmentPayload = () => {
    let slotInfo = selectedSlot;
    const duration = desiredDuration ?? selectedExpert?.[getDurationSettingKey(selectedType)] ?? APPOINTMENT_DEFAULT_DURATION;
    if (!slotInfo) {
      slotInfo = {
        startDate: desiredDateTime,
        endDate: new Date(new Date(desiredDateTime).getTime() + (duration * 60 * 1000)).toISOString(),
      };
    }

    const isVideoSpecialistAssessment = selectedType === VIDEO_SPECIALIST_ASSESSMENT;

    if (formType === FormTypes.PATIENT_FORM) {
      dispatch(createAppointmentRequest({
        startDate: slotInfo.startDate,
        endDate: slotInfo.endDate,
        appointmentType: selectedType,
        isUnrestricted: false,
        tenantIds,
        creatorId: userId,
        requesterId: userId,
        requestedIdList: [selectedExpert.id],
        appointmentInfo: {
          reason,
          shouldCreateVideoconferenceRoom: [VIDEO_CONSULTATION, VIDEO_SPECIALIST_ASSESSMENT].includes(selectedType),
        },
        resourceId: selectedLocation?.station_id,
        hasPayment: true,
      }));
    } else {
    // Many of the following fields may be undefined, depending on the type of appointment and delegation scenarios.
      dispatch(createAppointmentRequest({
        ...slotInfo,
        appointmentType: selectedType,
        isUnrestricted: isUnrestrictedForm || isLocationFormPremiocareTenant,
        tenantIds: isUnrestrictedForm ? tenantIds : undefined,
        creatorId: userId, // Current user (basically being or acting as a secretary)
        requesterId: selectedExpert?.id || userId, // The actual expert (requesting a meeting, or leading a teleconsultation), can be different from creatorId in delegation scenarios
        patientId: selectedPatient?.id,
        requestedIdList: isVideoSpecialistAssessment ? additionalSelectedExpertIdList : [selectedExpert?.id, ...additionalSelectedExpertIdList], // The experts requested for the meeting by the requester
        forceDisablePatientNotif: selectedType === OTHER && !isPatientNotifEnabled,
        appointmentInfo: {
          reason,
          shouldCreateVideoconferenceRoom: [VIDEO_CONSULTATION, VIDEO_SPECIALIST_ASSESSMENT].includes(selectedType), // Not taken into account by back end yet
          thirdPartyEmailList: isThirdPartyNotifEnabled ? thirdPartyEmailList : [],
          notifyPatientEmail: Boolean(selectedPatient?.email),
          notifyPatientSms: Boolean(selectedPatient?.phoneNumber),
        },
        resourceId: selectedLocation?.station_id, // A mobile unit
        hasPayment,
        consultationCodeId: desiredConsultationCode?.id,
      }));
    }
  };

  // Rendering management
  const isLoading = useSelector((state) => state.app.requestCounter[createAppointmentRequest.toString()] > 0);
  const classes = cx('appointmentForm', { appointmentForm_mobile: isSmall });

  return (
    <div className={classes}>
      <div className='appointmentForm-stepper'>
        {isLoading && <Loader position='absolute' isLoading={isLoading} />}
        <Stepper
          activeStep={activeStepIndex}
          orientation='vertical'
          sx={{ px: 2, py: 1 }}
          className='appointmentForm-stepperContainer'
        >
          {stepList.map(({ StepComponent, isLocked, isAfterExpertStep, shouldReset }, index) => {
            // Filter out the isAfterExpertStep prop before passing to StepComponent
            const stepProps = {
              isLocked,
              isCompleted: activeStepIndex > index,
              onStepClick: () => goBackToStep(index),
              onStepCompleted: nextStep,
              shouldReset: shouldReset || activeStepIndex < index,
              // This syntax avoids passing props to stepper components that don't need it (causing a console warning)
              ...(isAfterExpertStep !== undefined && { isAfterExpertStep }),
            };
            // TODO: use unique key defined alongside the steps instead of index
            return (<StepComponent key={index} {...stepProps} />);
          })}
        </Stepper>
      </div>
      <AppointmentSupport
        isHelpButton={isDialog || isSmall}
        disableBackButton={isDialog}
        onBackClick={forRightPanel ? onCreateAppointmentSettled : () => goBackToStep(activeStepIndex - 1)}
        content={stepList[activeStepIndex]?.supportContent}
      />
    </div>
  );
};

export default AppointmentForm;
